import { FC, useEffect, useMemo, useState } from 'react';

import { isNil } from 'lodash';
import { BsFillPencilFill } from 'react-icons/bs';
import { HiCheck } from 'react-icons/hi';
import { IoClose } from 'react-icons/io5';
import { CopyIcon } from 'ui/components/icon';
import { Dropdown } from 'ui/components/nav';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import { cn, copyToClipboard } from 'utils/helpers';

type VoidFunc = (val: string) => void;
type StringFunc = (val: string) => string;

type RuleType = VoidFunc | StringFunc;

interface PROPS {
  title: string;
  value?: string;
  rule?: RuleType;
  options: OPTIONS_PROPS[];
  onSubmit: (val: OPTIONS_PROPS, onSuccess: () => void) => void;
  isLoading?: boolean;
  disabled?: boolean;
  hideCopy?: boolean;
}

const QuickEditDropdown: FC<PROPS> = ({
  title,
  value: _value,
  onSubmit,
  rule,
  options,
  isLoading,
  disabled,
  hideCopy,
}) => {
  const [value, setValue] = useState(_value ?? '');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (_value) {
      setValue(_value);
    }
  }, [_value, options]);

  const handleSubmit = () => {
    if (rule) {
      const err = rule(value);

      if (err) {
        setError(err);
        return;
      }
    }

    onSubmit(options.find((opt) => opt?.value === value)!, () => {
      setIsEditMode(false);
      setError(null);
    });
  };

  const noValue = useMemo(() => {
    return `no ${title} yet`;
  }, [title]);

  return (
    <div className="border-b-[0.5px] border-b-separator-primary py-[14px]">
      <p className="text-sm secondary text-content">{title}</p>
      {isEditMode ? (
        <div className="flex mt-1 items-start">
          <div className="flex-1">
            <Dropdown
              defaultValue={_value}
              options={options}
              onChange={(opt) => {
                setValue(opt.value as string);
              }}
              optionsClassName="max-h-[250px] w-fit"
              selectedClassName="bg-transparent p-0 h-5 border-transparent"
            />
            {error && <p className="text-xs text-danger-base">{error}</p>}
          </div>
          <div className="flex items-center gap-x-2">
            <button
              onClick={handleSubmit}
              type="button"
              className={cn(
                'text-primary-base disabled:text-disabled',
                'bg-fill-secondary disabled:bg-transparent',
                'h-6 w-6 rounded-[6px]',
                'flex items-center justify-center',
              )}
            >
              <HiCheck className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIsEditMode(false)}
              type="button"
              className={cn(
                'h-6 w-6 rounded-[6px]',
                'bg-fill-secondary',
                'flex items-center justify-center',
                'shadow-leapify-sm',
              )}
            >
              <IoClose className="h-4 w-4 text-danger-base" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="flex items-center gap-1"
          onMouseEnter={() => setShowEdit(true)}
          onMouseLeave={() => setShowEdit(false)}
        >
          <p
            className={cn(
              'text-sm content primary',
              isNil(_value) && 'text-disabled italic lowercase',
            )}
          >
            {options.find((opt) => opt.value === _value)?.label ?? noValue}
          </p>
          {!isNil(_value) && !hideCopy && (
            <button
              disabled={isLoading}
              className="h-4"
              type="button"
              onClick={() => copyToClipboard(_value)}
            >
              <CopyIcon className="w-4 h-4 text-secondary-base" />
            </button>
          )}
          <button
            disabled={isLoading}
            type="button"
            className={cn(showEdit && !disabled ? 'block' : 'hidden')}
            onClick={() => setIsEditMode(true)}
          >
            <BsFillPencilFill className="w-3 h-3 text-secondary-base" />
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickEditDropdown;
